import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

// Admin-configured promo popup. Shown once the offer is switched on in the
// admin panel; the admin also picks how often a visitor who dismissed it sees
// it again. Dismissal is stored against the offer's revision, so editing the
// offer shows it to everyone again instead of staying hidden for past visitors.
const STORAGE_KEY = "fm_popup_dismissed";
const DAY_MS = 24 * 60 * 60 * 1000;

function readDismissal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // private mode / blocked storage — just show the popup
  }
}

function shouldShow(offer) {
  if (!offer?.is_active) return false;
  if (offer.frequency === "always") return true;

  const dismissed = readDismissal();
  if (!dismissed || dismissed.revision !== offer.revision) return true;
  if (offer.frequency === "once") return false;
  return Date.now() - (dismissed.at || 0) > DAY_MS; // 'daily'
}

export default function OfferPopup() {
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/popup-offer")
      .then((res) => {
        if (cancelled || !shouldShow(res.data)) return;
        setOffer(res.data);
        // Small delay so it doesn't fight the page's own first paint.
        setTimeout(() => !cancelled && setOpen(true), 1200);
      })
      .catch(() => {}); // a popup is never worth surfacing an error for
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ revision: offer?.revision, at: Date.now() }));
    } catch { /* storage blocked — it'll just show again next visit */ }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!open || !offer) return null;

  const onCta = () => {
    dismiss();
    const link = offer.cta_link || "/shop";
    if (/^https?:\/\//i.test(link)) window.location.href = link;
    else navigate(link);
  };

  return (
    <div className="fm-popup-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) dismiss(); }}>
      <style>{S}</style>
      <div className="fm-popup" role="dialog" aria-modal="true" aria-label={offer.title}>
        <button className="fm-popup-close" onClick={dismiss} aria-label="Close">×</button>

        {offer.image_url && <img src={offer.image_url} alt="" className="fm-popup-img" />}

        <div className="fm-popup-body">
          {offer.title && <h3 className="fm-popup-title">{offer.title}</h3>}
          {offer.message && <p className="fm-popup-msg">{offer.message}</p>}
          {offer.cta_text && (
            <button className="fm-popup-cta" onClick={onCta}>{offer.cta_text}</button>
          )}
        </div>
      </div>
    </div>
  );
}

const S = `
.fm-popup-overlay {
  position: fixed; inset: 0; z-index: 4000;
  background: rgba(20, 20, 20, 0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: fmPopupFade 0.2s ease;
}
@keyframes fmPopupFade { from { opacity: 0; } to { opacity: 1; } }
.fm-popup {
  position: relative;
  width: 100%; max-width: 420px;
  background: #fff; border-radius: 16px; overflow: hidden;
  box-shadow: 0 24px 60px rgba(0,0,0,0.3);
  animation: fmPopupIn 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  font-family: 'DM Sans', sans-serif;
}
@keyframes fmPopupIn { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: none; } }
.fm-popup-close {
  position: absolute; top: 10px; right: 10px; z-index: 2;
  width: 32px; height: 32px; border: none; border-radius: 50%;
  background: rgba(255,255,255,0.9); color: #444;
  font-size: 22px; line-height: 1; cursor: pointer;
}
.fm-popup-close:hover { background: #fff; color: #000; }
.fm-popup-img { width: 100%; max-height: 190px; object-fit: cover; display: block; }
.fm-popup-body { padding: 24px 22px 26px; text-align: center; }
.fm-popup-title {
  margin: 0 0 8px; font-size: 1.3rem; font-weight: 700; color: #1a1a1a;
  font-family: 'Playfair Display', serif;
}
.fm-popup-msg { margin: 0 0 18px; font-size: 0.9rem; line-height: 1.6; color: #5a5a5a; }
.fm-popup-cta {
  width: 100%; border: none; border-radius: 10px; padding: 12px 18px;
  background: #1a5c38; color: #fff; font-size: 0.92rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
}
.fm-popup-cta:hover { background: #164c2e; }
@media (max-width: 480px) { .fm-popup-title { font-size: 1.15rem; } }
`;
